// Embeds the IAP-served staff-nudge iframe on a customer checkout. The iframe
// renders content when the visitor has an active IAP staff session (the cross-site
// `iap_present` cookie, which only reaches the iframe in Chromium) or when the
// request comes from an SMG office network, which reaches every browser. For
// customers off that network, this is an invisible no-op.

declare interface myWindow extends Window {
    readonly apiRoot: string;
}
declare const window: myWindow;

// iapOrigin returns the origin that serves /iap/staff-nudge — the IAP v1 admin-api
// (api3.internetadminportal.com in prod). It is deliberately NOT the v3 frontend host:
// v3.internetadminportal.com is static Hugo output on S3, so the Go handler only answers
// on the admin-api host. In dev the browser hits the Go service directly. SMG_PORT_OFFSET
// shifts every service's port together, so rather than hardcode a per-site base port we
// derive the offset from the customer-api root the site already talks to (window.apiRoot,
// dev base 8085) and apply it to the admin-api dev base (8086). That keeps this helper
// host-independent — the multi-domain brands serve their domains on different dev ports.
function iapOrigin(): string {
    if (window.location.protocol === 'https:') {
        return 'https://api3.internetadminportal.com';
    }

    const customerAPIDevBasePort = 8085;
    const adminAPIDevBasePort = 8086;
    const portOffset = parseInt(new URL(window.apiRoot).port || '0', 10) - customerAPIDevBasePort;
    return `http://internetadminportal.localhost:${adminAPIDevBasePort + portOffset}`;
}

const IAP_ORIGIN = iapOrigin();

interface nudgeMessage {
    type: 'smg-staff-nudge';
    staff: boolean;
    height: number;
}

function isNudgeMessage(value: unknown): value is nudgeMessage {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const record = value as Record<string, unknown>;
    return record.type === 'smg-staff-nudge' &&
        typeof record.staff === 'boolean' &&
        typeof record.height === 'number';
}

export interface MountStaffNudgeOptions {
    // anchor is the element the iframe is inserted before — put the brand checkout's
    // top-level checkout element here so the banner lands above the checkout columns.
    anchor: Element;
    // cartID deep-links the "finish in IAP" banner back to the same cart in IAP.
    cartID: string;
}

// MountStaffNudge inserts the (initially zero-height, hidden) iframe before `anchor` and
// reveals it only when IAP posts back that this visitor is staff.
export function MountStaffNudge({ anchor, cartID }: MountStaffNudgeOptions): void {
    if (!anchor.parentElement) {
        return;
    }

    const $iframe = document.createElement('iframe');
    $iframe.src = `${IAP_ORIGIN}/iap/staff-nudge?cartID=${encodeURIComponent(cartID)}`;
    $iframe.title = 'IAP staff notice';
    $iframe.setAttribute('aria-hidden', 'true');
    $iframe.setAttribute('scrolling', 'no');
    $iframe.style.display = 'block';
    $iframe.style.width = '100%';
    $iframe.style.height = '0';
    $iframe.style.border = '0';
    $iframe.style.overflow = 'hidden';

    window.addEventListener('message', e => {
        if (e.origin !== IAP_ORIGIN) {
            return;
        }

        const message: unknown = e.data;
        if (!isNudgeMessage(message)) {
            return;
        }

        if (message.staff && message.height > 0) {
            $iframe.style.height = `${message.height}px`;
            $iframe.removeAttribute('aria-hidden');
        } else {
            $iframe.style.height = '0';
            $iframe.setAttribute('aria-hidden', 'true');
        }
    });

    anchor.parentElement.insertBefore($iframe, anchor);
}
